import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  Link
} from '@mui/material';
import {
  Code as CodeIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

/**
 * API Documentation Page
 * Links to PaceEstate API documentation
 */
const APIDocs = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';
  const swaggerUrl = `${apiUrl}/api/docs`;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <CodeIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              PaceEstate API Documentation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive REST API documentation with interactive testing
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            icon={<DescriptionIcon />}
            label="OpenAPI 3.0"
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            icon={<SecurityIcon />}
            label="JWT Authentication"
            color="success"
            variant="outlined"
            size="small"
          />
          <Chip
            label="Interactive Testing"
            color="info"
            variant="outlined"
            size="small"
          />
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>API Documentation</AlertTitle>
          Access the full interactive API documentation at the link below.
          Your JWT token will be needed for authenticated endpoints.
        </Alert>
      </Paper>

      {/* API Information Cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Getting Started
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1. Open the API documentation link below
            <br />
            2. Click "Authorize" and enter your JWT token
            <br />
            3. Browse available endpoints by namespace
            <br />
            4. Click "Try it out" to test endpoints
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Base URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {apiUrl}/api
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            All API endpoints are prefixed with /api
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Available Namespaces
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Authentication, Sales, Inventory, Clients, Leads, Invoices, Calendar, Notifications, Reports, Export
          </Typography>
        </Paper>
      </Stack>

      {/* Link to Swagger UI */}
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Open API Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Click the button below to access the full Swagger UI documentation
        </Typography>
        <Link
          href={swaggerUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 4,
            py: 2,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            textDecoration: 'none',
            fontSize: '1.1rem',
            '&:hover': {
              bgcolor: 'primary.dark',
            }
          }}
        >
          Open Swagger UI
          <OpenInNewIcon />
        </Link>
      </Paper>

      {/* Footer Information */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          PaceEstate API v1.0.0 | OpenAPI Specification | For internal use only
        </Typography>
      </Paper>
    </Container>
  );
};

export default APIDocs;
